// App.jsx -- Hub LAY01v1
// Routes consolidated: circular redirect chains eliminated.
// ASCII only. No Unicode.
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import SpawnReceiver from './components/SpawnReceiver.jsx';
import XRayOverlay from './components/XRayOverlay.jsx';
import { injectMobileStyles } from './utils/mobileLayout.js';
import { getAuthSession } from './lib/api.js';

// Lazy-loaded page components -- each becomes its own chunk
// lazyRetry: if chunk fails to load (stale hash after deploy), reload page once
function lazyRetry(importFn) {
  return lazy(function() {
    return importFn().catch(function(err) {
      var reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(function() {}); // never resolves -- page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      throw err;
    });
  });
}
var PortfolioView    = lazyRetry(function() { return import('./pages/PortfolioView.jsx'); });
var AgentManagement  = lazyRetry(function() { return import('./pages/AgentManagement.jsx'); });
var AgentMigration   = lazyRetry(function() { return import('./pages/AgentMigration.jsx'); });
var PartnerHub       = lazyRetry(function() { return import('./pages/PartnerHub.jsx'); });
var ClientsView      = lazyRetry(function() { return import('./pages/ClientsView.jsx'); });
var ClientDetail     = lazyRetry(function() { return import('./pages/ClientDetail.jsx'); });
var BugPanelPage     = lazyRetry(function() { return import('./components/BugPanel.jsx'); });
var StudiosStorefront = lazyRetry(function() { return import('./pages/StudiosStorefront.jsx'); });
var DesignSystem     = lazyRetry(function() { return import('./pages/DesignSystem/index.jsx'); });
var VisionDemo       = lazyRetry(function() { return import('./pages/VisionDemo.jsx'); });
var OnboardingView   = lazyRetry(function() { return import('./pages/OnboardingView.jsx'); });
var AdminView        = lazyRetry(function() { return import('./pages/AdminView.jsx'); });
var AgentProfileView = lazyRetry(function() { return import('./pages/AgentProfileView.jsx'); });
var CallsView        = lazyRetry(function() { return import('./pages/CallsView.jsx'); });
var InvestmentCommittee = lazyRetry(function() { return import('./pages/InvestmentCommittee.jsx'); });
var MyCompanies      = lazyRetry(function() { return import('./pages/platform/MyCompanies.jsx'); });
var AgentNetwork     = lazyRetry(function() { return import('./pages/AgentNetwork.jsx'); });
var NarrativeView    = lazyRetry(function() { return import('./pages/NarrativeView.jsx'); });
var ApprovalsQueue   = lazyRetry(function() { return import('./pages/ApprovalsQueue.jsx'); });
var PlatformOps      = lazyRetry(function() { return import('./pages/PlatformOps.jsx'); });
var WorkView         = lazyRetry(function() { return import('./pages/WorkView.jsx'); });
var InvestView       = lazyRetry(function() { return import('./pages/InvestView.jsx'); });
var InvestorPortal   = lazyRetry(function() { return import('./pages/InvestorPortal.jsx'); });
var JockeyPublic     = lazyRetry(function() { return import('./pages/JockeyPublic.jsx'); });
var MyHubView        = lazyRetry(function() { return import('./pages/MyHubView.jsx'); });
var OnboardingWizard = lazyRetry(function() { return import('./pages/OnboardingWizard.jsx'); });
var StorefrontBookkeeper = lazyRetry(function() { return import('./pages/StorefrontBookkeeper.jsx'); });
var ChangelogView    = lazyRetry(function() { return import('./pages/ChangelogView.jsx'); });
var ImportPage       = lazyRetry(function() { return import('./pages/ImportPage.jsx'); });
var AdminShell        = lazyRetry(function() { return import('./pages/AdminShell.jsx'); });
var AdminLensPopout   = lazyRetry(function() { return import('./pages/AdminLensPopout.jsx'); });
var AdminThreadPopout = lazyRetry(function() { return import('./pages/AdminThreadPopout.jsx'); });
var LearningView     = lazyRetry(function() { return import('./pages/LearningView.jsx'); });
var TeamOnboardingTour = lazyRetry(function() { return import('./pages/TeamOnboardingTour.jsx'); });
var ConnectToolsModal  = lazyRetry(function() { return import('./components/ConnectToolsModal.jsx'); });
var AnalyticsView    = lazyRetry(function() { return import('./pages/AnalyticsView.jsx'); });
var ProjectsDashboard = lazyRetry(function() { return import('./pages/ProjectsDashboard.jsx'); });
var FinanceOpsView   = lazyRetry(function() { return import('./pages/FinanceOpsView.jsx'); });
var LPPipelineView   = lazyRetry(function() { return import('./pages/LPPipelineView.jsx'); });
var HubView          = lazyRetry(function() { return import('./pages/HubView.jsx'); });
var SpacesView       = lazyRetry(function() { return import('./pages/SpacesView.jsx'); });
var CompanionView    = lazyRetry(function() { return import('./pages/CompanionView.jsx'); });
var PresentView      = lazyRetry(function() { return import('./pages/PresentView.jsx'); });
var OrgSettingsLens  = lazyRetry(function() { return import('./components/settings/OrgSettingsLens.jsx'); });
var HubUserSettingsLens = lazyRetry(function() { return import('./components/settings/HubUserSettingsLens.jsx'); });
var PersonalHubSettingsLens = lazyRetry(function() { return import('./components/settings/PersonalHubSettingsLens.jsx'); });
var PersonalHubUserSettingsLens = lazyRetry(function() { return import('./components/settings/PersonalHubUserSettingsLens.jsx'); });
var SettingsView = lazyRetry(function() { return import('./pages/SettingsView.jsx'); });
var CentralView = lazyRetry(function() { return import('./pages/CentralView.jsx'); });

var PAGE_FALLBACK = (
  <div style={{ padding: 40, textAlign: 'center', color: '#8B8FA3', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14 }}>
    Loading...
  </div>
);

// Redirects /agent/:agentId/chat -> /agent/:agentId (DEC-155)
function AgentChatRedirect() {
  var params = useParams();
  return <Navigate to={'/agent/' + params.agentId} replace />;
}

// Passes token param from router into ImportPage
function ImportPageRoute() {
  var params = useParams();
  return <ImportPage token={params.token} />;
}

// AdminFallback: catch-all for unknown /admin/* sub-paths (BUG-032).
// Renders the existing AdminShell so the user keeps full admin chrome and can
// recover via the rail, plus a fixed-position in-app 404 banner that displays
// the bad path. Replaces the prior blank-page failure mode for deep-links.
function AdminFallback() {
  var location = useLocation();
  var bad = location.pathname;
  return (
    <>
      <div role="status" aria-live="polite" style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9990,
        background: '#3A1F22',
        border: '1px solid #6B2C30',
        color: '#F8B5B8',
        padding: '8px 16px',
        borderRadius: 6,
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        maxWidth: '90vw',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        <strong style={{ marginRight: 8 }}>Page not found</strong>
        <span style={{ opacity: 0.8, fontFamily: "'IBM Plex Mono', monospace" }}>{bad}</span>
      </div>
      <AdminShell />
    </>
  );
}

// Demo entry page for partner presentations -- shows fake Google login, launches tour with Rikola persona
function TeamTourDemo() {
  var _s = useState('login');
  var screen = _s[0]; var setScreen = _s[1];
  var _p = useState('rikola');
  var persona = _p[0]; var setPersona = _p[1];
  var F = "'IBM Plex Sans', -apple-system, sans-serif";
  var M = "'IBM Plex Mono', monospace";
  var personas = [
    { id: 'rikola',  label: 'Rikola',  role: 'Platform Ops' },
    { id: 'clayton', label: 'Clayton', role: 'Capital' },
    { id: 'eugenio', label: 'Eugenio', role: 'Engineering' },
    { id: 'hugh',    label: 'Hugh',    role: 'CEO' }
  ];
  var activePer = personas.filter(function(p) { return p.id === persona; })[0] || personas[0];

  if (screen === 'tour') {
    return (
      <Suspense fallback={<div />}>
        <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 9999, display: 'flex', gap: 3, background: '#171A21', borderBottomLeftRadius: 8, padding: '4px 6px', border: '1px solid #2A2D35' }}>
          {personas.map(function(p) {
            var active = persona === p.id;
            return (
              <button key={p.id} onClick={function() { setPersona(p.id); setScreen('login'); }}
                style={{ padding: '3px 10px', borderRadius: 4, border: 'none', background: active ? '#4F6EF7' : 'transparent', color: active ? '#fff' : '#8B8FA3', fontSize: 10.5, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: F }}>
                {p.label}
              </button>
            );
          })}
        </div>
        <TeamOnboardingTour
          userName={activePer.label}
          userRole={activePer.role}
          onComplete={function() { setScreen('login'); }}
        />
      </Suspense>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0D0F14', fontFamily: F }}>
      {/* Persona selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 32, background: '#171A21', borderRadius: 8, padding: 4 }}>
        {personas.map(function(p) {
          var active = persona === p.id;
          return (
            <button key={p.id} onClick={function() { setPersona(p.id); }}
              style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: active ? '#4F6EF7' : 'transparent', color: active ? '#fff' : '#8B8FA3', fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: F }}>
              {p.label}
            </button>
          );
        })}
      </div>
      {/* Hub logo */}
      <svg width={48} height={48} viewBox="0 0 32 32" fill="none" style={{ marginBottom: 24 }}>
        <circle cx={16} cy={16} r={4} fill="#4F6EF7" />
        <circle cx={6} cy={10} r={2.5} fill="#4F6EF7" opacity={0.6} />
        <circle cx={26} cy={10} r={2.5} fill="#4F6EF7" opacity={0.6} />
        <circle cx={6} cy={22} r={2.5} fill="#4F6EF7" opacity={0.6} />
        <circle cx={26} cy={22} r={2.5} fill="#4F6EF7" opacity={0.6} />
        <line x1={8} y1={11} x2={13} y2={14} stroke="#4F6EF7" strokeWidth={1.2} opacity={0.4} />
        <line x1={24} y1={11} x2={19} y2={14} stroke="#4F6EF7" strokeWidth={1.2} opacity={0.4} />
        <line x1={8} y1={21} x2={13} y2={18} stroke="#4F6EF7" strokeWidth={1.2} opacity={0.4} />
        <line x1={24} y1={21} x2={19} y2={18} stroke="#4F6EF7" strokeWidth={1.2} opacity={0.4} />
        <circle cx={16} cy={16} r={14} stroke="#4F6EF7" strokeWidth={1} opacity={0.15} />
      </svg>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#E2E8F0', marginBottom: 4, fontFamily: F }}>Hub</div>
      <div style={{ fontSize: 14, color: '#8B8FA3', marginBottom: 6 }}>Your AI operating system</div>
      <div style={{ fontSize: 12, color: '#6B8AFF', marginBottom: 32, fontFamily: M }}>Logging in as {activePer.label} ({activePer.role})</div>
      <button onClick={function() { setScreen('tour'); }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 28px', borderRadius: 8, background: '#FFFFFF', color: '#1A1D23', fontSize: 14, fontWeight: 600, fontFamily: F, border: 'none', cursor: 'pointer' }}>
        <svg width={18} height={18} viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
        Sign in with Google
      </button>
      <div style={{ marginTop: 48, fontSize: 11, color: '#555', fontFamily: F }}>Sprint Mode, Inc.</div>
    </div>
  );
}

// SimpleLanding: inline landing page rendered at / for unauthenticated visitors.
// No redirects -- same URL, inline React component.
function SimpleLanding() {
  var F = "'IBM Plex Sans', -apple-system, sans-serif";
  var M = "'IBM Plex Mono', monospace";
  var _e = useState(''); var email = _e[0]; var setEmail = _e[1];
  var _sb = useState(false); var submitting = _sb[0]; var setSubmitting = _sb[1];
  var _sd = useState(false); var submitted = _sd[0]; var setSubmitted = _sd[1];
  function handleLogin() {
    fetch('/api/auth/login').then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok && d.data && d.data.url) window.location.href = d.data.url;
    });
  }

  function handleSubmit() {
    var e = email.trim();
    if (!e || e.indexOf('@') < 1) return;
    setSubmitting(true);
    fetch('/api/waitlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: e, source: 'heyhub_landing' })
    }).then(function() {
      setSubmitted(true);
    }).catch(function() {
      setSubmitted(true);
    });
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f7f4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F }}>
      <div style={{ maxWidth: 480, width: '100%', padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 40 }}>
          <svg width={40} height={40} viewBox="0 0 80 80" fill="none">
            <line x1="20" y1="27" x2="33" y2="36" stroke="#4F6EF7" strokeWidth="1.2" strokeLinecap="round"/>
            <line x1="60" y1="27" x2="47" y2="36" stroke="#4F6EF7" strokeWidth="1.2" strokeLinecap="round"/>
            <line x1="20" y1="53" x2="33" y2="44" stroke="#4F6EF7" strokeWidth="1.2" strokeLinecap="round"/>
            <line x1="60" y1="53" x2="47" y2="44" stroke="#4F6EF7" strokeWidth="1.2" strokeLinecap="round"/>
            <circle cx="15" cy="25" r="5.5" fill="#4F6EF7"/>
            <circle cx="65" cy="25" r="5.5" fill="#4F6EF7"/>
            <circle cx="15" cy="55" r="5.5" fill="#4F6EF7"/>
            <circle cx="65" cy="55" r="5.5" fill="#4F6EF7"/>
            <circle cx="40" cy="40" r="10" fill="#4F6EF7"/>
          </svg>
          <span style={{ fontSize: 22, fontWeight: 700, color: '#12110f', letterSpacing: '-0.3px' }}>Hub</span>
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: '#12110f', lineHeight: 1.2, margin: '0 0 32px 0' }}>
          Familiar UI.<br/><em style={{ color: '#4F6EF7', fontStyle: 'normal' }}>Unfamiliar Intelligence.</em>
        </h1>
        {submitted ? (
          <div style={{ fontSize: 15, color: '#4F6EF7', marginBottom: 24, padding: '12px 20px', background: '#f0f3ff', borderRadius: 8 }}>
            You're on the list. We'll be in touch.
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="email"
                value={email}
                onChange={function(ev) { setEmail(ev.target.value); }}
                onKeyDown={function(ev) { if (ev.key === 'Enter') handleSubmit(); }}
                placeholder="you@company.com"
                style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e3dc', borderRadius: 6, fontSize: 14, fontFamily: F, background: '#ffffff', color: '#12110f', outline: 'none' }}
              />
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ padding: '10px 20px', background: '#4F6EF7', color: '#ffffff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer', fontFamily: F }}
              >
                {submitting ? '...' : 'Notify Me'}
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#6b6962', margin: '0 0 24px 0' }}>Get early access when we launch.</p>
          </div>
        )}
        <a
          href="#"
          onClick={function(ev) { ev.preventDefault(); handleLogin(); }}
          style={{ fontFamily: M, fontSize: 11, color: '#8b8880', textDecoration: 'none', letterSpacing: '0.3px' }}
        >
          Log in
        </a>
      </div>
    </div>
  );
}

// AuthGuard: checks /api/auth/session on mount; redirects to /login if not authenticated.
// Also redirects to /welcome if onboarding not yet completed (once per user, cached in localStorage).
function AuthGuard(props) {
  var _s = useState('loading');
  var authState = _s[0]; var setAuthState = _s[1];
  var _emailRef = useRef(null);
  var location = useLocation();

  useEffect(function() {
    // Already on /welcome -- skip onboarding check to avoid loop
    if (location.pathname === '/welcome') {
      setAuthState('ok');
      return;
    }

    // Clear chunk-reload flag on successful load (resets ErrorBoundary retry state)
    try { sessionStorage.removeItem('hub_reload_attempted'); } catch(e) {}

    // Restore saved destination after OAuth redirect, then go to ok state
    function goOk() {
      var _rt = null;
      try { _rt = sessionStorage.getItem('hub_return_to'); if (_rt) sessionStorage.removeItem('hub_return_to'); } catch(e) {}
      if (_rt && _rt.length > 2) {
        window.location.href = _rt.replace(/^#/, '');
      }
      setAuthState('ok');
    }

    // Team tour gate: check if user needs team tour before proceeding to Hub
    function checkTourThenGo(email) {
      _emailRef.current = email;
      // Skip tour check for these paths
      var _path = window.location.pathname || '/';
      var SKIP_PATHS = ['/team-tour', '/welcome', '/login', '/onboarding', '/import', '/demo'];
      var _skip = false;
      for (var _pi = 0; _pi < SKIP_PATHS.length; _pi++) {
        if (_path.indexOf(SKIP_PATHS[_pi]) === 0) { _skip = true; break; }
      }
      if (_skip) { goOk(); return; }

      // Fast path: localStorage cache
      try {
        if (localStorage.getItem('hub_team_tour_done_' + email) === '1') {
          goOk();
          return;
        }
      } catch(e) {}

      // Check API
      fetch('/api/tour-guide/team-tour-status')
        .then(function(r) { return r.json(); })
        .then(function(res) {
          if (res.ok && res.data && res.data.needs_tour) {
            try { sessionStorage.setItem('hub_tour_user', JSON.stringify(res.data)); } catch(e) {}
            setAuthState('team_tour');
          } else {
            try { localStorage.setItem('hub_team_tour_done_' + email, '1'); } catch(e) {}
            goOk();
          }
        })
        .catch(function() { goOk(); }); // fail open
    }

    getAuthSession().then(function(res) {
      var email = res.data && res.data.email;
      // SKIP_ONBOARDING: never show welcome gate for these users
      var SKIP_ONBOARDING = ['aaron@sprintmode.co', 'etomaso@sprintmode.co'];
      if (email && SKIP_ONBOARDING.indexOf(email) >= 0) {
        try { localStorage.setItem('hub_onb_done_' + email, '1'); } catch(e) {}
        checkTourThenGo(email);
        return;
      }
      // Fast path: localStorage cache for completed users
      try {
        if (email && localStorage.getItem('hub_onb_done_' + email) === '1') {
          checkTourThenGo(email);
          return;
        }
      } catch(e) {}
      if (!email) { goOk(); return; }
      fetch('/api/onboarding/status')
        .then(function(r) { return r.json(); })
        .then(function(onbRes) {
          if (onbRes.ok && onbRes.data && !onbRes.data.completed) {
            if (onbRes.data.user_type === 'family') {
              checkTourThenGo(email);
            } else if (onbRes.data.user_type === 'invited') {
              setAuthState('connect_tools');
            } else {
              setAuthState('welcome');
            }
          } else {
            if (onbRes.ok && onbRes.data && onbRes.data.completed) {
              try { localStorage.setItem('hub_onb_done_' + email, '1'); } catch(e) {}
            }
            checkTourThenGo(email);
          }
        }).catch(function() {
          goOk(); // fail open on onboarding check error
        });
    }).catch(function() {
      // Save current destination before redirecting to login
      try { sessionStorage.setItem('hub_return_to', window.location.pathname + window.location.search); } catch(e) {}
      setAuthState('denied');
    });
  }, []);

  if (authState === 'loading') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: "'IBM Plex Sans', sans-serif", color: '#94A3B8', fontSize: '14px'
      }}>
        Loading...
      </div>
    );
  }
  if (authState === 'denied') {
    return <SimpleLanding />;
  }
  if (authState === 'welcome') {
    return <Navigate to="/onboarding-wizard" replace />;
  }
  if (authState === 'connect_tools') {
    return (
      <Suspense fallback={PAGE_FALLBACK}>
        <ConnectToolsModal
          title="Connect your personal tools"
          onComplete={function() {
            fetch('/api/onboarding/wizard/complete', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ hub_type: 'invited', selected_agents: [] })
            }).catch(function() {});
            try { localStorage.setItem('hub_onb_done_' + (_emailRef.current || ''), '1'); } catch(e) {}
            setAuthState('loading');
            checkTourThenGo(_emailRef.current);
          }}
          onSkip={function() {
            fetch('/api/onboarding/wizard/complete', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ hub_type: 'invited', selected_agents: [] })
            }).catch(function() {});
            try { localStorage.setItem('hub_onb_done_' + (_emailRef.current || ''), '1'); } catch(e) {}
            setAuthState('loading');
            checkTourThenGo(_emailRef.current);
          }}
        />
      </Suspense>
    );
  }
  if (authState === 'team_tour') {
    return (
      <Suspense fallback={PAGE_FALLBACK}>
        <TeamOnboardingTour onComplete={function() {
          try { localStorage.setItem('hub_team_tour_done_' + (_emailRef.current || ''), '1'); } catch(e) {}
          setAuthState('ok');
        }} />
      </Suspense>
    );
  }
  return props.children;
}

var ROUTE_META = {
  '/brief':                    { title: 'Daily Brief',       breadcrumb: ['Hub', 'Brief'] },
  '/portfolio':                { title: 'Portfolio',         breadcrumb: ['Hub', 'Portfolio'] },
  '/investor-hub':             { title: 'Investor Hub',      breadcrumb: ['Hub', 'Investor Hub'] },
  '/investor-hub/cap-table':   { title: 'Cap Table',         breadcrumb: ['Hub', 'Investor Hub', 'Cap Table'] },
  '/investor-hub/portfolio':   { title: 'Portfolio',         breadcrumb: ['Hub', 'Investor Hub', 'Portfolio'] },
  '/investor-hub/calendar':    { title: 'IR Calendar',       breadcrumb: ['Hub', 'Investor Hub', 'Calendar'] },
  '/investor-hub/content':     { title: 'Content',           breadcrumb: ['Hub', 'Investor Hub', 'Content'] },
  '/investor-hub/docs':        { title: 'Materials',         breadcrumb: ['Hub', 'Investor Hub', 'Materials'] },
  '/jockey':                   { title: 'Jockey Console',    breadcrumb: ['Hub', 'Jockey', 'Console'] },
  '/jockey/dashboard':         { title: 'Jockey Dashboard',  breadcrumb: ['Hub', 'Jockey', 'Dashboard'] },
  '/jockey/dev':               { title: 'Jockey Dev',        breadcrumb: ['Hub', 'Jockey', 'Dev'] },
  '/jockey/team':              { title: 'Jockey Team',       breadcrumb: ['Hub', 'Jockey', 'Team'] },
  '/jockey/skills':            { title: 'Skill Catalog',     breadcrumb: ['Hub', 'Jockey', 'Skills'] },
  '/jockey/patterns':          { title: 'Pattern Library',   breadcrumb: ['Hub', 'Jockey', 'Patterns'] },
  '/settings':                 { title: 'Your Settings',     breadcrumb: ['Hub', 'Settings'] },
  '/settings/org':             { title: 'Org Settings',          breadcrumb: ['Hub', 'Settings', 'Organization'] },
  '/settings/hub':             { title: 'Personal Hub Settings',  breadcrumb: ['Hub', 'Settings', 'Personal Hub'] },
  '/settings/me':              { title: 'Your Settings',          breadcrumb: ['Hub', 'Settings', 'You'] },
  '/settings/member':          { title: 'Member Settings',        breadcrumb: ['Hub', 'Settings', 'Member'] },
  '/vault':                    { title: 'Vault',             breadcrumb: ['Hub', 'Vault'] },
  '/org':                      { title: 'Organization',      breadcrumb: ['Hub', 'Organization'] },
  '/org/:section':             { title: 'Organization',      breadcrumb: ['Hub', 'Organization'] },
  '/approvals-queue':          { title: 'Approvals Queue',   breadcrumb: ['Hub', 'Approvals Queue'] },
  '/agents':                   { title: 'Agents',            breadcrumb: ['Hub', 'Agents'] },
  '/partner-hub':              { title: 'Partner Hub',       breadcrumb: ['Hub', 'Partner Hub'] },
  '/platform':                 { title: 'Platform',          breadcrumb: ['Hub', 'Platform'] },
  '/platform/dev':             { title: 'Dev Admin',         breadcrumb: ['Hub', 'Dev Admin'] },
  '/admin-legacy':             { title: 'Admin',             breadcrumb: ['Hub', 'Admin'] },
  '/agent-network':            { title: 'Agent Network',     breadcrumb: ['Hub', 'Agent Network'] },
  '/connectors':               { title: 'Connectors',        breadcrumb: ['Hub', 'Connectors'] },
  '/fundraise':                { title: 'Fundraise',         breadcrumb: ['Hub', 'Fundraise'] },
  '/calls':                    { title: 'Calls',             breadcrumb: ['Hub', 'Calls'] },
  '/clients':                  { title: 'Clients',           breadcrumb: ['Hub', 'Clients'] },
  '/clients/:id':              { title: 'Client Detail',     breadcrumb: ['Hub', 'Clients', 'Detail'] },

  '/spaces':                   { title: 'Spaces',            breadcrumb: ['Hub', 'Spaces'] },
  '/companion':                { title: 'Companion',         breadcrumb: ['Hub', 'Companion'] },
  '/present':                  { title: 'Present',           breadcrumb: ['Hub', 'Present'] },
  '/investment-committee':     { title: 'Investment Committee', breadcrumb: ['Hub', 'Investment Committee'] },
  '/clone':                    { title: 'Clone Comparison',  breadcrumb: ['Hub', 'Clone'] },
  '/narrative':                { title: 'Narrative',         breadcrumb: ['Hub', 'Narrative'] },
  '/threads':                  { title: 'Threads',           breadcrumb: ['Hub', 'Threads'] },
  '/conversations':            { title: 'Conversations',     breadcrumb: ['Hub', 'Conversations'] },
  '/work':                     { title: 'Work',              breadcrumb: ['Hub', 'Work'] },
  '/agent/:agentId/chat':      { title: 'Agent Chat',        breadcrumb: ['Hub', 'Agent', 'Chat'] },
};

function RouteWrapper() {
  var location = useLocation();
  var meta = ROUTE_META[location.pathname] || { title: 'Hub', breadcrumb: ['Hub'] };

  useEffect(function() {
    document.title = meta.title + ' | Hub';
  }, [meta.title]);

  var _slug = location.pathname.split('/')[1] || '';

  // Hub View routes: /d/{dept}, /d/{dept}/{agent}, /l/{category} (PD48 Hub View S7)
  if (_slug === '' || _slug === 'd' || _slug === 'l') {
    return (
      <Suspense fallback={PAGE_FALLBACK}>
        <HubView />
      </Suspense>
    );
  }

  // Legacy agent slugs → redirect to Hub View /d/ URLs
  var LEGACY_REDIRECTS = {
    'daily': '/', 'hub': '/', 'brief': '/',
    'cfo': '/d/finance/cfo', 'bookkeeper': '/d/finance/bookkeeper',
    'collections': '/d/finance/collections', 'payroll': '/d/finance/payroll',
    'sales-director': '/d/sales/sales-director', 'sdr': '/d/sales/sdr',
    'engineering-manager': '/d/engineering/engineering-manager',
    'project-manager': '/d/operations/projects',
    'customer-success': '/d/customer-success/customer-success',
    'people-hr': '/d/people/people', 'security': '/d/it/security',
    'compliance': '/d/legal/governance', 'fund-counsel': '/d/legal/fund-counsel',
    'fundraise': '/d/investor-relations/fundraise',
    'governance': '/d/legal/governance',
    'marketing': '/d/marketing/editorial', 'content': '/d/marketing/editorial',
    'data-analyst': '/d/engineering/engineering-manager',
    'triage': '/d/operations/operations', 'chief-of-staff': '/d/executive/chief-of-staff',
    'coo': '/d/operations/operations',
    'threads': '/l/threads', 'favorites': '/l/lenses', 'lenses': '/l/lenses',
    'artifacts': '/l/artifacts', 'vault': '/l/vault',
    'finance': '/d/finance', 'books': '/d/finance', 'reconciliation': '/d/finance',
    'pipeline': '/d/sales', 'crm': '/d/sales'
  };
  if (LEGACY_REDIRECTS[_slug]) {
    return <Navigate to={LEGACY_REDIRECTS[_slug]} replace />;
  }

  if (_slug === 'platform') {
    return <Navigate to="/admin" replace />;
  }

  // Unified settings bypasses Layout (PD48c-S3 -- personal + hub admin)
  if (_slug === 'settings' && location.pathname === '/settings') {
    return (
      <Suspense fallback={PAGE_FALLBACK}>
        <Routes>
          <Route path="/settings" element={<SettingsView />} />
        </Routes>
      </Suspense>
    );
  }

  // Unified platform + dev admin (PD48c-S4)
  if (_slug === 'admin') {
    return (
      <Suspense fallback={PAGE_FALLBACK}>
        <Routes>
          <Route path="/admin" element={<AdminShell />} />
          <Route path="/admin/lens/:lensId" element={<AdminLensPopout />} />
          <Route path="/admin/thread/:threadId" element={<AdminThreadPopout />} />
          <Route path="/platform/lens/:lensId" element={<Navigate to="/admin" replace />} />
          <Route path="/platform/thread/:threadId" element={<Navigate to="/admin" replace />} />
          {/* Admin sub-paths: AdminShell reads lens from URL */}
          <Route path="/admin/*" element={<AdminShell />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Layout routeMeta={meta}>
      <Suspense fallback={PAGE_FALLBACK}>
        <Routes>
          {/* ---- Primary destinations ---- */}
          <Route path="/team-tour"                 element={<TeamOnboardingTour />} />
          <Route path="/brief"                     element={<Navigate to="/" replace />} />
          <Route path="/portfolio"                 element={<PortfolioView />} />
          {/* FA1-UNIFY-S1: Redirect old investor-hub routes to unified agent_ir view */}
          <Route path="/investor-hub"              element={<Navigate to="/agent/agent_ir?section=roster" replace />} />
          <Route path="/investor-hub/cap-table"    element={<Navigate to="/agent/agent_ir?section=cap_table" replace />} />
          <Route path="/investor-hub/portfolio"    element={<Navigate to="/agent/agent_ir?section=portfolio" replace />} />
          <Route path="/investor-hub/calendar"     element={<Navigate to="/agent/agent_ir?section=calendar" replace />} />
          <Route path="/investor-hub/content"      element={<Navigate to="/agent/agent_ir?section=content" replace />} />
          <Route path="/investor-hub/docs"         element={<Navigate to="/agent/agent_ir?section=docs" replace />} />
          <Route path="/jockey"                    element={<Navigate to="/agent/agent_engineering_manager" replace />} />
          <Route path="/jockey/*"                  element={<Navigate to="/agent/agent_engineering_manager" replace />} />
          <Route path="/settings/org"              element={<OrgSettingsLens />} />
          <Route path="/settings/hub"              element={<PersonalHubSettingsLens />} />
          <Route path="/settings/me"               element={<HubUserSettingsLens />} />
          <Route path="/settings/member"           element={<PersonalHubUserSettingsLens />} />
          {/* BUG-065: catch-all for unknown /settings/* -- render SettingsView shell with in-app fallback (default Profile pane) instead of bouncing to /. */}
          <Route path="/settings/*"                element={<SettingsView />} />
          <Route path="/org"                       element={<Navigate to="/admin" replace />} />
          <Route path="/org/:section"              element={<Navigate to="/admin" replace />} />
          <Route path="/approvals-queue"           element={<ApprovalsQueue />} />
          <Route path="/agents"                    element={<AgentManagement />} />
          <Route path="/migrate-agent"             element={<AgentMigration />} />
          <Route path="/partner-hub"               element={<PartnerHub />} />
          <Route path="/connectors"                element={<PlatformOps />} />
          <Route path="/conversations"             element={<Navigate to="/threads" replace />} />
          <Route path="/work"                      element={<WorkView />} />
          <Route path="/fundraise"                 element={<Navigate to="/d/investor-relations/fundraise" replace />} />
          <Route path="/lp-pipeline"               element={<Suspense fallback={PAGE_FALLBACK}><LPPipelineView /></Suspense>} />
          <Route path="/calls"                     element={<CallsView />} />
          {/* /voice and /voice-log are kept as redirects: header mic buttons in Layout.jsx now */}
          {/* dispatch 'hub:trigger-mic' event handled by DailyView (C53-HEADER-MIC-FIX-S1, DEC-C49-06). */}
          <Route path="/voice"                     element={<Navigate to="/" replace />} />
          <Route path="/voice-log"                 element={<Navigate to="/" replace />} />
          <Route path="/changelog"                 element={<ChangelogView />} />
          <Route path="/projects"                  element={<Suspense fallback={PAGE_FALLBACK}><ProjectsDashboard /></Suspense>} />
          <Route path="/finance-ops"               element={<Suspense fallback={PAGE_FALLBACK}><FinanceOpsView /></Suspense>} />
          <Route path="/clients"                   element={<ClientsView />} />
          <Route path="/clients/:id"               element={<ClientDetail />} />
          <Route path="/agent-network"             element={<AgentNetwork />} />
          <Route path="/narrative"                 element={<NarrativeView />} />
          <Route path="/central-legacy"             element={<CentralView />} />
          <Route path="/spaces"                    element={<Suspense fallback={PAGE_FALLBACK}><SpacesView /></Suspense>} />
          <Route path="/companion"                 element={<Suspense fallback={PAGE_FALLBACK}><CompanionView /></Suspense>} />
          <Route path="/present"                   element={<Suspense fallback={PAGE_FALLBACK}><PresentView /></Suspense>} />
          <Route path="/investment-committee"      element={<InvestmentCommittee />} />
          <Route path="/clone"                     element={<Navigate to="/admin" replace />} />
          <Route path="/admin-legacy"              element={<AdminView />} />
          <Route path="/admin-legacy/agents"       element={<AdminView section="agents" />} />
          <Route path="/admin-legacy/users"        element={<AdminView section="users" />} />
          <Route path="/admin-legacy/vision"       element={<AdminView section="vision" />} />
          <Route path="/admin-legacy/design"       element={<AdminView section="design" />} />
          <Route path="/admin-legacy/agents/:agentId" element={<AgentProfileView />} />
          <Route path="/admin-legacy/clones"       element={<Navigate to="/admin" replace />} />
          <Route path="/admin-legacy/tenants"      element={<AdminView section="tenants" />} />
          <Route path="/admin-legacy/governance"   element={<AdminView section="governance" />} />
          <Route path="/admin-legacy/d1"           element={<AdminView section="d1" />} />
          <Route path="/admin-legacy/runners"      element={<AdminView section="runners" />} />
          <Route path="/learning"                  element={<Suspense fallback={PAGE_FALLBACK}><LearningView /></Suspense>} />
          <Route path="/bugs"                      element={<Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>}><BugPanelPage visible={true} standalone={true} /></Suspense>} />

          <Route path="/agent/:agentId"            element={<Navigate to="/" replace />} />
          <Route path="/agent/:agentId/chat"       element={<AgentChatRedirect />} />

          {/* ---- Redirects: all direct to final destination (no chains) ---- */}
          <Route path="/platform-ops"    element={<Navigate to="/connectors" replace />} />
          <Route path="/slack"           element={<Navigate to="/connectors" replace />} />
          <Route path="/analytics"       element={<Suspense fallback={<div/>}><AnalyticsView /></Suspense>} />
          <Route path="/superinsight"    element={<Navigate to="/brief" replace />} />
          <Route path="/team"            element={<Navigate to="/brief" replace />} />

          {/* Legacy flat slug redirects -> Hub View department URLs (PD48) */}
          <Route path="/cfo"              element={<Navigate to="/d/finance/cfo" replace />} />
          <Route path="/bookkeeper"       element={<Navigate to="/d/finance/bookkeeper" replace />} />
          <Route path="/collections"      element={<Navigate to="/d/finance/collections" replace />} />
          <Route path="/sales-director"   element={<Navigate to="/d/sales/sales-director" replace />} />
          <Route path="/sdr"              element={<Navigate to="/d/sales/sdr" replace />} />
          <Route path="/engineering-manager" element={<Navigate to="/d/engineering/engineering-manager" replace />} />
          <Route path="/chief-of-staff"   element={<Navigate to="/d/executive/chief-of-staff" replace />} />
          <Route path="/coo"              element={<Navigate to="/d/operations/operations" replace />} />
          <Route path="/governance"       element={<Navigate to="/d/legal/governance" replace />} />
          <Route path="/customer-success" element={<Navigate to="/d/customer-success/customer-success" replace />} />
          <Route path="/project-manager"  element={<Navigate to="/d/operations/projects" replace />} />
          <Route path="/people-hr"        element={<Navigate to="/d/people/people" replace />} />
          <Route path="/payroll"          element={<Navigate to="/d/finance/payroll" replace />} />
          <Route path="/security"         element={<Navigate to="/d/it/security" replace />} />
          <Route path="/compliance"       element={<Navigate to="/d/legal/governance" replace />} />
          <Route path="/fund-counsel"     element={<Navigate to="/d/legal/fund-counsel" replace />} />
          <Route path="/marketing"        element={<Navigate to="/d/marketing/editorial" replace />} />
          <Route path="/content"          element={<Navigate to="/d/marketing/editorial" replace />} />
          <Route path="/data-analyst"     element={<Navigate to="/d/engineering/engineering-manager" replace />} />
          <Route path="/threads"          element={<Navigate to="/l/threads" replace />} />
          <Route path="/lenses"           element={<Navigate to="/l/lenses" replace />} />
          <Route path="/artifacts"        element={<Navigate to="/l/artifacts" replace />} />
          <Route path="/vault"            element={<Navigate to="/l/vault" replace />} />
          <Route path="/favorites"        element={<Navigate to="/l/lenses" replace />} />

          <Route path="*"  element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function AuthenticatedApp() {
  return (
    <AuthGuard>
      <RouteWrapper />
    </AuthGuard>
  );
}

export default function App() {
  useEffect(function() {
    injectMobileStyles();
    try { sessionStorage.removeItem('chunk_reload'); } catch(e) {}
  }, []);

  var _isInvestorsDomain = typeof window !== 'undefined' && (
    window.location.hostname === 'investors.sprintmode.co' ||
    window.location.hostname === 'investors.sprintmode.ai' ||
    window.location.hostname === 'investors.heyhub.ai'
  );

  if (_isInvestorsDomain) {
    return (
      <BrowserRouter>
        <Suspense fallback={PAGE_FALLBACK}>
          <Routes>
            <Route path="*" element={<InvestorPortal />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <>
      <BrowserRouter>
        <SpawnReceiver />
        <Suspense fallback={PAGE_FALLBACK}>
          <Routes>
            <Route path="/login"          element={<Navigate to="/brief" />} />
            <Route path="/studios-public" element={<StudiosStorefront />} />
            <Route path="/onboarding/:name" element={<OnboardingView />} />
            <Route path="/design-system"  element={<DesignSystem />} />
            <Route path="/vision"         element={<VisionDemo />} />
            <Route path="/my-companies"   element={<MyCompanies />} />
            <Route path="/landing"        element={<Navigate to="/" replace />} />

            <Route path="/invest/:token"  element={<InvestView />} />
            <Route path="/jockey-public"  element={<JockeyPublic />} />
            <Route path="/my-hubs"          element={<MyHubView />} />
            <Route path="/onboarding-wizard" element={<OnboardingWizard />} />
            <Route path="/bookkeeperos" element={<StorefrontBookkeeper />} />
            <Route path="/import/:token" element={<ImportPageRoute />} />
            <Route path="/demo/onboard"  element={<TeamTourDemo />} />
            <Route path="*"               element={<AuthenticatedApp />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      {/* XRayOverlay removed */}
    </>
  );
}

// Global focus reset injected at module level
if (typeof document !== 'undefined') {
  var _focusStyle = document.createElement('style');
  _focusStyle.textContent = '* { outline: none !important; } *:focus { outline: none !important; box-shadow: none !important; } textarea:focus, input:focus, button:focus { outline: none !important; box-shadow: none !important; }';
  document.head.appendChild(_focusStyle);
}
